import { displayAlert } from 'src/utilities/display-alert.js';
import { getStore } from 'src/store';
import { leaveBreadcrumb, notifyError, recordPageView, setMonitoringUser } from 'src/utilities/monitoring.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import type { LitElement } from 'lit';
import { installRouter } from 'pwa-helpers/router.js';
import { initActions, loginUserAction, updatePath, updateUserAction } from 'src/store/actions';
import { LocalDateTime } from 'src/utilities/local-date-time.js';
import {
  checkRedirectCodeFlow,
  getValidAccessToken,
  getRefreshToken,
  isRefreshTokenExpired,
} from 'src/store/oauth-browser';
import {
  AUTH_USERNAME_KEY,
  clearAuthStorage,
  clearLogoutReturnState,
  clearPendingCriiptoLogoutReturn,
  clearPostLogoutProvider,
  clearPostLogoutRedirectToSignin,
  getPersistedAuthIdentity,
  getPersistedAuthState,
  getStoredLogoutReturnState,
  persistLogoutReturnState,
} from 'src/auth/auth-storage.js';
import {
  getPostLogoutStorageState,
  getLogoutReturnRedirect,
  planLogout,
  shouldForcePromptLoginAfterLogoutError,
  shouldSkipLogoutReturnHandling,
  shouldObserveLogoutReturn,
} from 'src/auth/criipto-logout.js';
import { runningUnderNativePlatform } from 'src/store/running-under-native-platform.js';
import { createClient } from './store/backend';
import { BASE_PATH } from './store/config.js';
import { checkAndClearStaleCache, clearCache } from 'src/store/cache-cleanup';
import { bootStep } from 'src/utilities/boot-step.js'; /**
 *
 * The redux store contains a normalized view of the current organization.
 *
 * All entities are at the top level under organization.
 *
 *
 *
 */

/**
 *
 * The redux store contains a normalized view of the current organization.
 *
 * All entities are at the top level under organization.
 *
 *
 *
 */

console.log('Initializing dabih store at ' + LocalDateTime.now().toString());
initActions((error: Error) => {
  // Add response status metadata for ResponseError
  const responseError = error as any;
  if (responseError && (responseError.status || responseError.response?.status)) {
    const status = responseError.status || responseError.response?.status;
    return notifyError(error, {
      metadata: {
        response: {
          status: status,
          statusText: responseError.response?.statusText,
        },
      },
    });
  }
  return notifyError(error);
}, displayAlert);

// Create an instance of reduxConnectMixin
// export const reduxConnectMixin = createMixin(getStore());

type GConstructor<T = unknown> = new (...args: any[]) => T;

export function reduxConnectMixin<TBase extends GConstructor<LitElement>>(Base: TBase) {
  const d = getStore().dispatch;
  type TestParams = Parameters<typeof d>;
  type FirstParam = TestParams[0];
  return class reduxElement extends connect(getStore())(Base) {
    dispatchAction(args: FirstParam) {
      return d(args);
    }
  };
}

window.document.addEventListener<any>('navigate', async (e: CustomEvent<{ href: string }>) => {
  window.history.pushState({}, '', e.detail.href);
  await handleNavigation(window.location);
});

export function getCurrentLogoutOptions(): {
  provider?: string;
  username?: string;
} {
  const user = getStore().getState().user;
  const persistedIdentity = getPersistedAuthIdentity();

  return {
    provider: user?.provider ?? persistedIdentity.provider,
    username: user?.username ?? persistedIdentity.username,
  };
}

export async function handleNavigation(location: Location) {
  window.scrollTo(0, 0);
  console.log('Navigation ' + location.href);
  if (location.pathname === '/iframe.html') {
    console.log('Ignoring redux navigation for storybook rendering');
    return;
  }

  // Record a parameterised page view for CloudWatch RUM on every navigation.
  recordPageView();

  const initialLogoutMarkers = getStoredLogoutReturnState();
  const initialLogoutReturnState = {
    pathname: location.pathname,
    search: location.search,
    ...initialLogoutMarkers,
  };
  const skipLogoutReturnHandling = shouldSkipLogoutReturnHandling(initialLogoutReturnState);
  const forcePromptLoginAfterLogoutError = shouldForcePromptLoginAfterLogoutError(initialLogoutReturnState);

  if (skipLogoutReturnHandling) {
    clearLogoutReturnState();
  }

  const logoutReturnState =
    skipLogoutReturnHandling && forcePromptLoginAfterLogoutError
      ? initialLogoutReturnState
      : {
          pathname: location.pathname,
          search: location.search,
          ...getStoredLogoutReturnState(),
        };
  const observedLogoutReturn = shouldObserveLogoutReturn(logoutReturnState);

  if (observedLogoutReturn) {
    leaveBreadcrumb('Logout return observed', {
      provider: logoutReturnState.postLogoutProvider,
    });
    clearPostLogoutProvider();
  }

  const logoutReturnRedirect = getLogoutReturnRedirect(logoutReturnState);

  if (logoutReturnRedirect !== undefined) {
    clearPostLogoutRedirectToSignin();
    clearPendingCriiptoLogoutReturn();
    window.history.replaceState({}, '', logoutReturnRedirect);
    await handleNavigation(window.location);
    return;
  }

  if (observedLogoutReturn) {
    clearPendingCriiptoLogoutReturn();
  }

  if (location.pathname === '/') {
    console.log('[redux-behavior.handleNavigation] On root path, calling checkRedirectCodeFlow...');
    // OAuth-retur (code i URL) trenger flere sekvensielle nettverkskall
    // (token-exchange + login) og får derfor romsligere frist enn vanlig boot,
    // der steget bare er en eventuell token-refresh. Ved timeout fortsetter
    // innlastingen; fullfører login-utvekslingen i bakgrunnen, redirecter den
    // selv til '/'.
    const oauthReturn = new URLSearchParams(location.search).has('code');
    await bootStep('redirect-code-flow', () => checkRedirectCodeFlow(), {
      timeoutMs: oauthReturn ? 45_000 : 15_000,
      fallback: undefined,
    });
    console.log('[redux-behavior.handleNavigation] checkRedirectCodeFlow completed');

    // Check if cache should be cleared due to expired refresh token
    // This ensures stale sessions don't persist cached data.
    // Best effort: Safari-IDB-requests kan henge evig, og opprydding er ikke
    // verdt en hengende innlasting - hopp over ved timeout (#13438).
    const cacheCleared = await bootStep('stale-cache-cleanup', () => checkAndClearStaleCache(), {
      timeoutMs: 5_000,
      fallback: false,
    });
    if (cacheCleared) {
      console.log('[redux-behavior.handleNavigation] Stale cache cleared');
    }
  }

  const path = decodeURIComponent(location.pathname);
  const query = location.search.slice(1);
  const params = new URLSearchParams(query);
  const m: Record<string, string> = {};
  params.forEach((v, k) => {
    m[k] = v;
  });

  const { username, hasPersistedAuth } = await getPersistedAuthState();
  const hasUser = hasPersistedAuth;

  // const user = await currentUser();

  // console.log('cognito', user);
  console.log(username, hasUser, path);
  if (hasUser) {
    console.log('User: ' + username + ' ' + path);
    const usernameFromLocalStorage = localStorage.getItem(AUTH_USERNAME_KEY);
    console.log('Local Storage: ' + usernameFromLocalStorage);
    getStore().dispatch(updatePath(path, m));
    const u = getStore().getState().user;
    if (u === undefined) {
      console.log('[redux-behavior handleNavigation] No user in state, calling loginUserAction for:', username);
      try {
        const result = await getStore().dispatch(loginUserAction(createClient(), username ?? ''));
        console.log('[redux-behavior handleNavigation] loginUserAction result:', result);
        if (!result) {
          // Only logout if refresh token is actually expired
          // This prevents logout on transient errors (offline, 500, 502, etc.)
          const refreshTokenValue = await getRefreshToken();
          if (refreshTokenValue && !isRefreshTokenExpired(refreshTokenValue)) {
            console.log('[redux-behavior handleNavigation] Login failed but refresh token valid - staying logged in');
          } else {
            console.log('[redux-behavior handleNavigation] Login failed and refresh token expired - logging out');
            logOut(false, getCurrentLogoutOptions());
          }
        }
      } catch (e) {
        console.error(JSON.stringify(e));
      }
    }

    const targetUrl = sessionStorage.getItem('targetUrl');
    if (targetUrl !== null) {
      console.log('Redirecting to targetUrl ' + targetUrl);
      sessionStorage.removeItem('targetUrl');
      setTimeout(async () => {
        window.history.pushState({}, '', targetUrl);
        await handleNavigation(window.location);
      }, 0);
    }
  } else if (path === '/link') {
    getStore().dispatch(updatePath(path, m));
    console.log('Navigate to link');
  } else if (path === '/signup') {
    getStore().dispatch(updatePath(path, m));
    console.log('Navigate to signup');
  } else if (path === '/me') {
    getStore().dispatch(updatePath(path, m));
    console.log('Navigate to me');
  } else {
    console.log('No user, navigate to signin');
    window.history.pushState({}, '', '/signin' + location.search);
    getStore().dispatch(updatePath('/signin', m));
  }

  // window.scrollTo({ top: 0, left: 0 });
}

export function logOut(
  redirectToSignin: boolean,
  options?: {
    provider?: string;
    username?: string;
  },
) {
  const plan = planLogout({
    provider: options?.provider,
    redirectToSignin,
    origin: window.location.origin,
    basePath: BASE_PATH,
    criiptoIdpLogoutActive: !runningUnderNativePlatform(),
  });
  console.log('Log out', redirectToSignin, options?.provider);
  setMonitoringUser();
  leaveBreadcrumb('Logout path selected', {
    provider: options?.provider || 'unknown',
    logoutPath: plan.logoutPath,
    redirectToSignin,
  });

  try {
    getStore().dispatch(updateUserAction(undefined));
  } catch (error) {
    notifyError(error);
  }

  setTimeout(() => {
    const postLogoutStorageState = getPostLogoutStorageState(plan);
    Promise.all([clearCache(), clearAuthStorage()]).then(() => {
      persistLogoutReturnState(postLogoutStorageState);
      window.location.href = plan.destination;
    });
  }, 15);
}

// Globale app-bootstrap-side-effekter (router-installasjon + token-heartbeat).
// Hoppes over under test (vitest): ellers floater de auth-/localStorage-promiser
// og holder en åpen timer etter at jsdom-miljøet rives ned, noe som ga unhandled
// rejections i komponenttester (#13012). I prod/dev kjører de som før.
if (import.meta.env.MODE !== 'test') {
  installRouter((location) => handleNavigation(location));

  // Token refresh heartbeat - keeps access token fresh
  // ROBUST MODE: Only logout if refresh token actually expired, not on network/server errors
  setInterval(async () => {
    try {
      const accessToken = await getValidAccessToken();
      if (accessToken) {
        console.log('[Heartbeat] Access token refreshed successfully');
      } else {
        // getValidAccessToken() returnerer '' uten å kaste når det ikke finnes
        // tokens (utlogget) eller refresh feilet - ikke logg suksess da, det
        // maskerer auth-tilstanden når vi feilsøker med brukerlogger (#13438).
        console.log('[Heartbeat] No valid access token (logged out or refresh failed) - nothing refreshed');
      }
    } catch (e) {
      console.error('[Heartbeat] Token refresh failed:', e);

      // Don't logout immediately on error - check if refresh token is still valid
      // This handles: offline, server errors, timeouts, bad network, etc.
      const refreshToken = await getRefreshToken();
      if (refreshToken && !isRefreshTokenExpired(refreshToken)) {
        console.log('[Heartbeat] Refresh failed but token still valid - staying logged in');
        console.log('[Heartbeat] User can continue using cached data');
        return; // Don't logout
      }

      // Only logout if refresh token is actually expired
      console.log('[Heartbeat] Refresh token expired - logging out');
      logOut(true, getCurrentLogoutOptions());
    }
  }, 30000);
}
